import { useEffect, useState } from 'react';

const PROBE_INTERVAL_MS = 5000;
const PROBE_TIMEOUT_MS = 5000;

/**
 * Whether our API answers at all. `no-cors` makes any response count, a 404 or an opaque one
 * included, so only a network failure or the timeout reads as unreachable — a CORS rule can never
 * fake an outage.
 */
export async function isApiReachable(url: string, timeoutMs: number = PROBE_TIMEOUT_MS): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    await fetch(url, { method: 'HEAD', mode: 'no-cors', cache: 'no-store', signal: controller.signal });

    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * The browser's online/offline events, plus a probe of our own API while `probe` is on.
 *
 * This replaces react-detect-offline, which pinged https://httpbin.org/get every 5 seconds from
 * every open tab on Windows and Linux desktops — the visitor's IP to a third party 12 times a
 * minute. Desktop Chrome and Firefox keep reporting navigator.onLine while the router has no
 * internet, so a probe is still needed — but only once a request of ours has already failed.
 */
export default function useNetworkStatus(probe: boolean): { online: boolean; apiReachable: boolean | null } {
  const [browserOnline, setBrowserOnline] = useState<boolean>(() =>
    typeof navigator.onLine === 'boolean' ? navigator.onLine : true,
  );
  const [apiReachable, setApiReachable] = useState<boolean | null>(null);

  useEffect(() => {
    const goOnline = () => setBrowserOnline(true);
    const goOffline = () => setBrowserOnline(false);

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  useEffect(() => {
    if (!probe) {
      setApiReachable(null);

      return;
    }

    let active = true;

    const run = async () => {
      const reachable = await isApiReachable(import.meta.env.VITE_API_BASE_URL);

      if (active) {
        setApiReachable(reachable);
      }
    };

    run();
    const intervalId = setInterval(run, PROBE_INTERVAL_MS);

    return () => {
      active = false;
      clearInterval(intervalId);
    };
  }, [probe]);

  return { online: browserOnline && apiReachable !== false, apiReachable };
}
